<template>
  <div>
    <ChatBubble v-if="!showChat" />
    <div class="resizeable-area">
      <transition name="chat-open">
        <DxResizable
          id="chatContainer"
          :keep-aspect-ratio="false"
          :min-width="minWidth"
          :min-height="minHeight"
          :max-height="maxHeight"
          :max-width="maxWidth"
          handles="left top topleft"
          :class="showChat ? 'borderVisible' : ''"
          v-if="showChat"
          @resize="onChatResize"
        >
          <div class="chat-container" v-show="showChat">
            <div class="row chat-options justify-between">
              <span class="chat-title bold-font">{{ t("chat.title") }}</span>
              <div class="chat-icon-options-row row justify-center">
                <i
                  class="dx-icon-copy chat-icon-option"
                  @click="copyEntireChat"
                  id="copyChatIcon"
                  :aria-label="t('chat.copy')"
                ></i>
                <DxTooltip
                  :hide-on-outside-click="false"
                  target="#copyChatIcon"
                  show-event="mouseenter"
                  hide-event="mouseleave"
                  position="top"
                  :container="'body'"
                >
                  {{ t("chat.copy") }}
                </DxTooltip>
                <i
                  class="dx-icon-refresh chat-icon-option"
                  @click="refreshChat"
                  id="refreshChatIcon"
                  :aria-label="t('chat.refresh')"
                ></i>
                <DxTooltip
                  :hide-on-outside-click="false"
                  target="#refreshChatIcon"
                  show-event="mouseenter"
                  hide-event="mouseleave"
                  position="top"
                  :container="'body'"
                >
                  {{ t("chat.refresh") }}
                </DxTooltip>
                <i
                  class="dx-icon-fullscreen chat-icon-option"
                  @click="increaseChatSize"
                  id="increaseSizeIcon"
                  :aria-label="t('chat.increase_size')"
                  v-if="!isChatMaximized"
                ></i>
                <DxTooltip
                  :hide-on-outside-click="false"
                  target="#increaseSizeIcon"
                  show-event="mouseenter"
                  hide-event="mouseleave"
                  position="top"
                  :container="'body'"
                  v-if="!isChatMaximized"
                >
                  {{ t("chat.increase_size") }}
                </DxTooltip>

                <i
                  class="las la-compress la-2x chat-icon-option"
                  @click="decreaseChatSize"
                  id="decreaseSizeIcon"
                  :aria-label="t('chat.decrease_size')"
                  v-if="isChatMaximized"
                ></i>
                <DxTooltip
                  :hide-on-outside-click="false"
                  target="#decreaseSizeIcon"
                  show-event="mouseenter"
                  hide-event="mouseleave"
                  position="top"
                  :container="'body'"
                  v-if="isChatMaximized"
                >
                  {{ t("chat.decrease_size") }}
                </DxTooltip>

                <i
                  class="las la-2x la-history chat-icon-option"
                  @click="showHistoryPopup"
                  id="showHistoryBtn"
                  :aria-label="t('chat.chat_history')"
                ></i>
                <DxTooltip
                  :hide-on-outside-click="false"
                  target="#showHistoryBtn"
                  show-event="mouseenter"
                  hide-event="mouseleave"
                  position="top"
                  :container="'body'"
                >
                  {{ t("chat.chat_history") }}
                </DxTooltip>

                <i
                  class="dx-icon-close chat-icon-option"
                  @click="closeChat"
                  id="closeChatIcon"
                  :aria-label="t('chat.close')"
                ></i>
                <DxTooltip
                  :hide-on-outside-click="false"
                  target="#closeChatIcon"
                  show-event="mouseenter"
                  hide-event="mouseleave"
                  position="top"
                  :container="'body'"
                >
                  {{ t("chat.close") }}
                </DxTooltip>
              </div>
            </div>

            <div
              :id="chatUploadDropZoneId"
              ref="chatBoxRef"
              :aria-label="t('chat.upload_drop_zone')"
              class="chat-box"
              :class="{ 'chat-box-drop-active': isDropZoneActive }"
              @drop.capture="handleChatDropCapture"
            >
              <div
                v-if="isDropZoneActive"
                class="chat-drop-overlay"
                aria-hidden="true"
              >
                <i class="dx-icon-upload chat-drop-icon"></i>
                <span>{{ t("chat.drop_file_to_attach") }}</span>
              </div>
              <DxChat
                ref="dxChatRef"
                :data-source="chatDataSource"
                :reload-on-change="false"
                :user="currentUser"
                :showAvatar="true"
                :showUserName="true"
                :showDayHeaders="true"
                :showMessageTimestamp="true"
                :dayHeaderFormat="'dd/MM/yyyy'"
                :messageTimestampFormat="'hh:mm a'"
                message-template="message"
                @message-entered="onMessageEntered"
                :typingUsers="typingUsers"
                class="chat-content"
                @attachment-download-click="onAttachmentDownloadClick"
                ><template #message="{ data }">
                  <div class="chat-message-wrapper">
                    <div
                      v-if="data.message?.text"
                      class="chat-message-text"
                      v-html="renderMessage(data.message)"
                    ></div>
                    <i
                      :class="
                        copiedMessageId === data.id
                          ? 'dx-icon dx-icon-check copy-icon'
                          : 'dx-icon dx-icon-copy copy-icon'
                      "
                      @click="
                        copyMessageWithFeedback(
                          data.id,
                          renderMessage(data.message),
                        )
                      "
                      :title="
                        copiedMessageId === data.id ? t('chat.copied') : t('chat.copy_message')
                      "
                    ></i>
                  </div>
                </template>
                <DxFileUploaderOptions
                  :accept="acceptedAttachmentExtensions"
                  :allowed-file-extensions="allowedAttachmentExtensions"
                  drop-zone="#chatUploadDropZone"
                  :multiple="false"
                  :on-drop-zone-enter="handleDropZoneEnter"
                  :on-drop-zone-leave="clearDropZoneActive"
                  :on-upload-error="handleUploadError"
                  :upload-file="uploadFile"
                />
              </DxChat>
            </div>
          </div>
        </DxResizable>
      </transition>
    </div>
  </div>
</template>

<script setup>
import {
  computed,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  ref,
} from "vue";
import store from "../../store/index";
import { useI18n } from "vue-i18n";
import ChatBubble from "./ChatBubble.vue";
import { DxChat, DxFileUploaderOptions } from "devextreme-vue/chat";
import { DxTooltip } from "devextreme-vue/tooltip";
import DxResizable from "devextreme-vue/resizable";
import moment from "moment";
import AiAssistantService from "../../services/aiAssistant.service";
import MarkdownIt from "markdown-it";
import createChatStream from "../../services/sse.service";
import {
  chatDataSource,
  reloadChatDataSource,
  syncChatDataSource,
} from "../../store/modules/custom.store";
import { useDelayedRenderer } from "../../helpers/delayedRenderer";
import { Guid } from "devextreme-vue/common";
import aiAssistantService from "../../services/aiAssistant.service";

const md = new MarkdownIt({
  html: true,
  linkify: true,
  breaks: true,
});

const { t, locale } = useI18n();
const typingUsers = computed(() => store.state.chat.typingUsers);
const noResultsText = t("chat.no_results");
const showChat = computed({
  get: () => store.state.chat.showChat,
});
const renderMessage = (message) => {
  if (!message || !message.text) return "";

  return md.render(message.text);
};

const maxHeight = ref(0);
const maxWidth = ref(0);
const isStreaming = ref(false);
const activeStream = ref(null);
const activeRenderer = ref(null);
const isStoppingStream = ref(false);
const chatBoxRef = ref(null);
const dxChatRef = ref(null);
const pendingAttachments = ref([]);
const defaultAllowedAttachmentExtensions = [".doc", ".docx", ".pdf"];
const allowedAttachmentExtensions = ref([...defaultAllowedAttachmentExtensions]);
const chatUploadDropZoneId = "chatUploadDropZone";
const isDropZoneActive = ref(false);
const acceptedAttachmentExtensions = computed(() =>
  allowedAttachmentExtensions.value.join(","),
);

const pushChatChanges = (changes) => {
  syncChatDataSource(changes);
};

const syncInsertedMessage = (message) => {
  pushChatChanges([{ type: "insert", data: message }]);
};

const syncUpdatedMessage = (messageId, data) => {
  pushChatChanges([{ type: "update", key: messageId, data }]);
};

const syncRemovedMessage = (messageId) => {
  pushChatChanges([{ type: "remove", key: messageId }]);
};

const getChatRootElement = () =>
  dxChatRef.value?.$el ?? chatBoxRef.value?.$el ?? chatBoxRef.value ?? null;

const getScrollContainer = () => {
  const root = getChatRootElement();
  if (!root) return null;

  const candidates = [
    root.querySelector(".dx-scrollable-container"),
    root.querySelector(".dx-scrollview-content")?.parentElement ?? null,
    root.querySelector(".dx-chat-message-list"),
  ];

  return candidates.find((candidate) => candidate instanceof HTMLElement) ?? null;
};

const scrollToBottom = async () => {
  await nextTick();

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      const scrollContainer = getScrollContainer();
      if (!scrollContainer) return;

      scrollContainer.scrollTop = scrollContainer.scrollHeight;
    });
  });
};

const finalizeStreamingState = () => {
  activeRenderer.value = null;
  activeStream.value = null;
  store.dispatch("chat/clearStreamingState");
  isStoppingStream.value = false;
  isStreaming.value = false;
};

const insertAssistantPlaceholder = async () => {
  const assistantId = new Guid().toString();
  const message = await store.dispatch(
    "chat/insertAssistantPlaceholder",
    assistantId,
  );
  syncInsertedMessage(message);
  scrollToBottom();
  return message;
};

const ensureAssistantSession = async () => {
  if (
    !store.state.chat.aiAssistantAccessToken.accessToken ||
    moment(store.state.chat.aiAssistantAccessToken.expirationTime).isBefore(
      moment().add(1, "minute"),
    )
  ) {
    const response = await AiAssistantService.getAIAssistantAccessToken();

    if (response.status === 200) {
      await store.dispatch(
        "chat/setAIAssistantAccessToken",
        response.data,
        t("chat.error_occurred"),
      );
    }
  }
};

const stopStreaming = () => {
  activeRenderer.value?.stop();
  if (activeStream.value) {
    isStoppingStream.value = true;
    activeStream.value.close();
    return;
  }

  finalizeStreamingState();
};

const updateLastMessageText = (assistantMessageId, text) => {
  if (!assistantMessageId) return;

  store.dispatch("chat/updateAssistantMessageText", {
    assistantMessageId,
    text,
  });
  syncUpdatedMessage(assistantMessageId, { text });
  scrollToBottom();
};

const removeMessage = (messageId) => {
  if (!messageId) return;

  store.dispatch("chat/removeMessageById", messageId);
  syncRemovedMessage(messageId);
};

const updateViewportLimits = () => {
  maxWidth.value = window.innerWidth * 0.9;
  maxHeight.value = window.innerHeight * 0.9;
};

const minWidth = computed(() => {
  if (window.innerWidth < 820) {
    return window.innerWidth * 0.9;
  }
  return 720;
});

const minHeight = computed(() => {
  if (window.innerHeight < 820) {
    return window.innerHeight * 0.9;
  }
  return 620;
});

onMounted(() => {
  updateViewportLimits();
  window.addEventListener("resize", updateViewportLimits);
  scrollToBottom();
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateViewportLimits);
  stopStreaming();
});

const refreshChat = async () => {
  stopStreaming();
  pendingAttachments.value = [];
  store.dispatch("chat/resetChat");
  store.dispatch("chat/startNewConversation");
  await reloadChatDataSource();
  scrollToBottom();
};

const closeChat = () => {
  store.commit("chat/updateShowChat", false);
};

const isChatMaximized = ref(false);

const onChatResize = (e) => {
  if (e.width >= maxWidth.value - 10 && e.height >= maxHeight.value - 10)
    isChatMaximized.value = true;
  else isChatMaximized.value = false;
};

const decreaseChatSize = () => {
  const el = document.getElementById("chatContainer");
  if (!el) return;

  el.classList.add("animate-resize");

  el.style.width = `${minWidth.value}px`;
  el.style.height = `${minHeight.value}px`;
  isChatMaximized.value = false;

  setTimeout(() => {
    el.classList.remove("animate-resize");
  }, 200);
};

const increaseChatSize = () => {
  const el = document.getElementById("chatContainer");
  if (!el) return;
  /*
  Added this class (animate-resize) to do a simple animation when clicking button
  to increase size. Needs to be removed because if always kept, it messes with
  the resizing of chat when dragging,
  */
  el.classList.add("animate-resize");

  el.style.width = `${maxWidth.value}px`;
  el.style.height = `${maxHeight.value}px`;
  isChatMaximized.value = true;
  setTimeout(() => {
    el.classList.remove("animate-resize");
  }, 200);
};

onBeforeMount(async () => {
  const initializationTasks = [loadAttachmentSettings()];

  if (store.state.chat.settings === null)
    initializationTasks.push(AiAssistantService.getAISettings());

  await Promise.all(initializationTasks);
  await store.dispatch("chat/initChatFromSession");
});

const currentUser = computed({
  get: () => store.state.chat.currentUser,
});

const createUserMessage = (text, attachments = [], attachmentIds = []) => {
  const message = {
    id: new Guid().toString(),
    timestamp: new Date(),
    author: store.state.chat.currentUser,
    text,
    conversationId: store.state.chat.currentConversationId,
  };

  if (attachments.length) {
    message.attachments = attachments.map((attachment) => ({
      attachmentId: attachment.attachmentId,
      name: attachment.name,
      size: attachment.size,
    }));
  }

  if (attachmentIds.length) {
    message.attachmentIds = [...attachmentIds];
  }

  return message;
};

const normalizeAttachment = (attachment) => ({
  attachmentId: attachment.attachmentId,
  name: attachment.name,
  size: attachment.size,
});

const resolveMessageAttachments = (enteredMessageAttachments = []) => {
  const availableAttachments = [...pendingAttachments.value];
  const attachmentCandidates = enteredMessageAttachments.length
    ? enteredMessageAttachments
    : availableAttachments;

  const resolvedAttachments = [];
  const resolvedAttachmentIds = [];

  attachmentCandidates.forEach((attachment) => {
    const matchIndex = availableAttachments.findIndex(
      (candidate) =>
        candidate.name === attachment.name && candidate.size === attachment.size,
    );

    if (matchIndex === -1) {
      return;
    }

    const [matchedAttachment] = availableAttachments.splice(matchIndex, 1);
    resolvedAttachments.push(normalizeAttachment(matchedAttachment));
    resolvedAttachmentIds.push(matchedAttachment.attachmentId);
  });

  return {
    attachments: resolvedAttachments,
    attachmentIds: resolvedAttachmentIds,
  };
};

const insertUserMessage = (message) => {
  store.dispatch("chat/insertUserMessage", message);
  syncInsertedMessage(message);
  scrollToBottom();
};

const syncCurrentConversationWelcomeMessage = () => {
  const messages = currentConversationMessages.value ?? [];
  const welcomeMessage = messages[messages.length - 1];

  if (!welcomeMessage) return;

  syncInsertedMessage(welcomeMessage);
};

const currentConversationMessages = computed(() => {
  const currentConversationId = store.state.chat.currentConversationId;
  return currentConversationId
    ? store.state.chat.messagesByConversation[currentConversationId] ?? []
    : [];
});

const onMessageEntered = async (e) => {
  const message = e?.message?.text ?? e?.text ?? "";

  const enteredMessageAttachments = Array.isArray(e?.message?.attachments)
    ? e.message.attachments
    : [];
    
  if (!message && enteredMessageAttachments.length === 0) return;
  const { attachments, attachmentIds } = resolveMessageAttachments(
    enteredMessageAttachments,
  );
  const userMessage = createUserMessage(message, attachments, attachmentIds);
  pendingAttachments.value = [];
  insertUserMessage(userMessage);
  const conversationSnapshot = [...currentConversationMessages.value];

  isStreaming.value = true;

  const requestStreamId = async () => {
    await ensureAssistantSession();

    const streamId = await AiAssistantService.processPrompt(
      conversationSnapshot,
      locale.value
    );

    if (!streamId) {
      throw new Error(t("chat.error_occurred"));
    }

    store.commit("chat/setStreamId", streamId);
    return streamId;
  };

  try {
    const streamId = await requestStreamId();
    let buffer = "";
    let assistantMessage = null;
    let assistantMessagePromise = null;

    const ensureAssistantMessage = async () => {
      if (assistantMessage) return assistantMessage;

      if (!assistantMessagePromise) {
        assistantMessagePromise = insertAssistantPlaceholder().then((message) => {
          assistantMessage = message;
          activeRenderer.value = useDelayedRenderer((chunk) => {
            buffer += chunk;
            updateLastMessageText(assistantMessage.id, buffer);
          });

          return message;
        });
      }

      return assistantMessagePromise;
    };

    if (activeStream.value) activeStream.value.close();
    activeStream.value = createChatStream({
      streamId,
      onMessage: async (chunk) => {
        const message = await ensureAssistantMessage();
        activeRenderer.value?.pushChunk(chunk);
        return message;
      },
      onError: async (streamError) => {
        const errorText = typeof streamError === "string" && streamError
          ? streamError
          : noResultsText;

        activeRenderer.value?.stop();
        if (assistantMessage) {
          updateLastMessageText(assistantMessage.id, errorText);
        } else {
          await store.dispatch("chat/addAssistantMessage", errorText);
          syncInsertedMessage(
            currentConversationMessages.value[
              currentConversationMessages.value.length - 1
            ],
          );
        }
        finalizeStreamingState();
        store.dispatch("chat/startNewConversation");
        syncCurrentConversationWelcomeMessage();
        scrollToBottom();
      },
      onAbort: () => {
        activeRenderer.value?.stop();
        if (!buffer && assistantMessage) {
          removeMessage(assistantMessage.id);
        }

        if (isStoppingStream.value) {
          finalizeStreamingState();
          return;
        }

        finalizeStreamingState();
      },
      onComplete: async ({ empty }) => {
        activeRenderer.value?.stop({ flush: true });
        if (empty) {
          if (assistantMessage) {
            updateLastMessageText(assistantMessage.id, noResultsText);
          } else {
            await store.dispatch("chat/addAssistantMessage", noResultsText);
            syncInsertedMessage(
              currentConversationMessages.value[
                currentConversationMessages.value.length - 1
              ],
            );
          }
        }

        // Langdock sends the unique message when moderation error occurs on their side, so a new conversation has to be started in that case
        if (buffer && buffer === t('chat.error_occurred')) {
          store.dispatch("chat/startNewConversation");
          syncCurrentConversationWelcomeMessage();
        }

        finalizeStreamingState();
        scrollToBottom();
      },
    });
  } catch (error) {
    activeRenderer.value?.stop();
    store.dispatch("chat/addAssistantMessage", t("chat.error_occurred"));
    syncInsertedMessage(
      currentConversationMessages.value[currentConversationMessages.value.length - 1],
    );
    finalizeStreamingState();
    store.dispatch("chat/startNewConversation");
    syncCurrentConversationWelcomeMessage();
    scrollToBottom();
  }
};

const showHistoryPopup = () => {
  store.commit("chat/updateIsHistoryPopupVisible", true);
  closeChat();
};

const stripHtml = (html) => {
  const temp = document.createElement("div");
  temp.innerHTML = html;
  return temp.innerText;
};
const copiedMessageId = ref(null);

const copyMessageWithFeedback = async (id, htmlContent) => {
  const textContent = stripHtml(htmlContent);

  copiedMessageId.value = id;

  setTimeout(() => {
    copiedMessageId.value = null;
  }, 2000);

  try {
    if (navigator.clipboard && window.ClipboardItem) {
      const htmlBlob = new Blob([htmlContent], { type: "text/html" });
      const textBlob = new Blob([textContent], { type: "text/plain" });

      await navigator.clipboard.write([
        new window.ClipboardItem({
          "text/html": htmlBlob,
          "text/plain": textBlob,
        }),
      ]);

      return;
    }

    if (navigator.clipboard) {
      await navigator.clipboard.writeText(textContent);
      return;
    }

    const textarea = document.createElement("textarea");
    textarea.value = textContent;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);

    textarea.focus();
    textarea.select();
    document.execCommand("copy");

    document.body.removeChild(textarea);
  } catch (copyError) {
    store.commit("user/updateModalError", t("chat.copy_failed")),
      store.commit("user/enableModalError");
  }
};

const copyEntireChat = async () => {
  if (!chatBoxRef.value) return;

  const html = chatBoxRef.value.innerHTML;
  const text = chatBoxRef.value.innerText;

  try {
    if (navigator.clipboard && window.ClipboardItem) {
      await navigator.clipboard.write([
        new window.ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([text], { type: "text/plain" }),
        }),
      ]);
    } else if (navigator.clipboard) {
      await navigator.clipboard.writeText(text);
    } else {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
  } catch (err) {
    store.commit("user/updateModalError", t("chat.copy_failed")),
      store.commit("user/enableModalError");
  }
};

const getUploadErrorMessage = (error) => {
  const responseData = error?.response?.data;

  if (typeof responseData === "string" && responseData) {
    return responseData;
  }

  if (responseData?.message) {
    return responseData.message;
  }

  if (responseData?.Message) {
    return responseData.Message;
  }

  if (typeof error?.message === "string" && error.message) {
    return error.message;
  }

  return t("chat.error_occurred");
};

const handleUploadError = (event) => {
  event.message = getUploadErrorMessage(event?.error);
};

const getNativeDragEvent = (event) => event?.originalEvent ?? event;

const getDraggedFileCount = (event) => {
  const nativeEvent = getNativeDragEvent(event);
  const dataTransfer = nativeEvent?.dataTransfer;

  if (!dataTransfer) return 0;
  if (dataTransfer.files?.length) return dataTransfer.files.length;
  if (!dataTransfer.items?.length) return 0;

  let fileCount = 0;

  for (let index = 0; index < dataTransfer.items.length; index += 1) {
    if (dataTransfer.items[index].kind === "file") fileCount += 1;
  }

  return fileCount;
};

const isChatDropZoneEvent = (event) => {
  const dropZoneElement = event?.dropZoneElement;

  return (
    dropZoneElement?.id === chatUploadDropZoneId ||
    dropZoneElement?.get?.(0)?.id === chatUploadDropZoneId
  );
};

const showSingleAttachmentError = () => {
  store.commit("user/updateModalError", t("chat.single_file_upload_only"));
  store.commit("user/enableModalError");
};

const clearDropZoneActive = () => {
  isDropZoneActive.value = false;
};

const handleDropZoneEnter = (event) => {
  if (!isChatDropZoneEvent(event)) return;

  const draggedFileCount = getDraggedFileCount(event?.event);
  isDropZoneActive.value = draggedFileCount <= 1;
};

const handleChatDropCapture = (event) => {
  const droppedFileCount = getDraggedFileCount(event);

  clearDropZoneActive();

  if (droppedFileCount <= 1) return;

  event.preventDefault();
  event.stopPropagation();
  showSingleAttachmentError();
};

const loadAttachmentSettings = async () => {
  try {
    const attachmentSettings = await AiAssistantService.getAttachmentSettings();
    const configuredExtensions =
      attachmentSettings?.allowedAttachmentExtensions;

    if (Array.isArray(configuredExtensions) && configuredExtensions.length) {
      allowedAttachmentExtensions.value = configuredExtensions;
    }
  } catch (error) {
    allowedAttachmentExtensions.value = [...defaultAllowedAttachmentExtensions];
  }
};

/* Enable when the DevExtreme v26.1 is available. Use the :send-button-options attribute on the DxChat component and bind it to the following property
const sendButtonOptions = computed<DxChatTypes.SendButtonProperties>(() =>
  isStreaming.value
    ? { action: "custom", icon: "stopfilled", onClick: stopStreaming }
    : { action: "send", icon: "arrowright", onClick: () => {} }
);*/

const uploadFile = async (file, progressCallback) => {
  try {
    await ensureAssistantSession();

    const formData = new FormData();
    formData.append("file", file);

    const response = await AiAssistantService.uploadAttachment(formData);
    const attachmentId = response?.data?.attachmentId;

    if (!attachmentId) {
      throw new Error(t("chat.error_occurred"));
    }

    pendingAttachments.value = [
      {
        attachmentId: attachmentId,
        name: file.name,
        size: file.size,
      },
    ];

    progressCallback(file.size);

    return response.data;
  } catch (error) {
    throw new Error(getUploadErrorMessage(error));
  }
};

const onAttachmentDownloadClick = async (event) => {
  await ensureAssistantSession();
  await downloadAttachment(event.attachment.attachmentId, event.attachment.name);
};

const downloadAttachment = async (attachmentId, name) => {
  await aiAssistantService.downloadAttachment(attachmentId)
    .then((response) => {
      const fileURL = window.URL.createObjectURL(
        new Blob([response.data], { type: response.data.type })
      );
      let a = document.createElement("a");
      a.setAttribute("style", "display:none;");
      a.download = name;
      a.href = fileURL;
      a.target = "_blank";
      a.click();
    })
    .catch(async (error) => {
      const data = error.response?.data;

      if (data instanceof Blob) {
        const message = await data.text();
        store.commit("user/updateModalError", message);
      } else store.commit("user/updateModalError", error.message);

      store.commit("user/enableModalError");
    });
};
</script>

<style scoped lang="scss">
.options {
  padding: 20px;
  display: flex;
  flex-direction: column;
  min-width: 280px;
  background-color: rgba(191, 191, 191, 0.15);
  gap: 16px;
}

.caption {
  font-size: 14px;
  font-weight: 500;
}

.option-separator {
  border-bottom: 1px solid #0a108f;
}

.dx-avatar {
  border: 1px solid #0a108f;
}

#chatContainer {
  width: 720px;
  height: 620px;
  bottom: 20px;
  right: 20px;
  position: fixed;
  z-index: 999;
  max-height: 90vh;
  max-width: 93vw;
  /*
  Note: Keep this, translate(0,0) will keep chat in fixed position
  if a better solution is found I'll implement it.
  */
  transform: translate(0, 0) !important;
}
#chatContainer.animate-resize {
  transition: width 0.2s ease, height 0.2s ease;
}
.borderVisible {
  border-radius: 14px;
  background: white;
  overflow: hidden;
  border: 1px solid #e0e0e0;
}

.chat-open-enter-active {
  transition: opacity 0.35s ease-out, transform 0.35s ease-out;
}

.chat-open-enter-from {
  opacity: 0;
  transform: scale(0.96);
}

.chat-open-enter-to {
  opacity: 1;
  transform: scale(1);
}

.chat-open-leave-active {
  transition: none;
}

#chatContainer {
  transform-origin: top left;
}

.chat-box {
  position: relative;
}

.chat-box-drop-active {
  outline: 2px dashed #0a108f;
  outline-offset: -8px;
}

.chat-drop-overlay {
  align-items: center;
  background: rgba(222, 246, 254, 0.92);
  color: #0a108f;
  display: flex;
  flex-direction: column;
  font-size: 16px;
  font-weight: 600;
  gap: 8px;
  inset: 0;
  justify-content: center;
  pointer-events: none;
  position: absolute;
  text-align: center;
  z-index: 2;
}

.chat-drop-icon {
  font-size: 32px;
}
</style>
